import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, Table } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import { itemFamilyAPI } from '../api/itemFamilyAPI'
import { stockItemAPI } from '../api/stockItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { highlightText } from '../utils/commonUtils'
import { usePermissions } from '../contexts/PermissionContext'
import ConfirmationModal from '../components/ConfirmationModal'
import CopyableText from '../components/CopyableText'

const UOM_COMPAT_GROUPS = [
    ['bag', 'bulk', 'ct', 'jar', 'tote_bag'],
    ['tub'],
    ['film'],
    ['pallet'],
]

const getCompatibleUoms = (uom) => {
    if (!uom) return ''
    const group = UOM_COMPAT_GROUPS.find(g => g.includes(uom))
    return group ? group.join(',') : uom
}

function ItemFamilyUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const mode = id ? 'update' : 'create'

    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(mode === 'update')
    const [itemFamily, setItemFamily] = useState(null)
    const [name, setName] = useState('')

    // State for adding items (only used in update mode)
    const [selectedStockItem, setSelectedStockItem] = useState(null)
    const [addingItem, setAddingItem] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const searchTimerRef = useRef(null)

    useEffect(() => {
        if (mode === 'update') {
            fetchItemFamily()
        }
    }, [id, mode])

    const fetchItemFamily = async () => {
        try {
            setLoading(true)
            const data = await itemFamilyAPI.getById(id)
            setItemFamily(data)
            setName(data.name)
        } catch (error) {
            console.error('Error fetching item family:', error)
            setError('Failed to load item family data')
        } finally {
            setLoading(false)
        }
    }

    // Derive the family's UOM from existing members
    const familyUom = itemFamily?.stock_items?.[0]?.uom || null

    const loadStockItemOptions = useCallback((inputValue, callback) => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }
        if (!inputValue || inputValue.length < 2) {
            callback([])
            return
        }
        searchTimerRef.current = setTimeout(async () => {
            try {
                const response = await stockItemAPI.getAll(1, 50, inputValue, '', '', '', '', '', getCompatibleUoms(familyUom))
                const memberIds = new Set(itemFamily?.stock_items?.map(i => i._id) || [])
                const results = (response.results || []).filter(item => {
                    if (memberIds.has(item._id)) return false
                    if (item.item_family && item.item_family !== parseInt(id)) return false
                    return true
                })
                callback(results)
            } catch (error) {
                console.error('Error searching stock items:', error)
                callback([])
            }
        }, 300)
    }, [itemFamily?.stock_items, id, familyUom])

    const submitHandler = async () => {
        if (!name.trim()) {
            setError('Name is required')
            return
        }
        try {
            if (mode === 'create') {
                const newFamily = await itemFamilyAPI.create({ name })
                navigate(`/item-family/${newFamily._id}`)
            } else {
                await itemFamilyAPI.update(id, { name })
                navigate('/item-families')
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleRemoveItem = async (stockItemId) => {
        if (!window.confirm('Are you sure you want to remove this item from the family?')) {
            return
        }

        try {
            await itemFamilyAPI.removeItem(id, stockItemId)
            await fetchItemFamily()
        } catch (error) {
            console.error('Error removing item from family:', error)
            alert(`Failed to remove item: ${error.response?.data?.error || error.message}`)
        }
    }

    const handleAddItem = async () => {
        if (!selectedStockItem) return

        try {
            setAddingItem(true)
            await itemFamilyAPI.addItem(id, selectedStockItem._id)
            await fetchItemFamily()
            setSelectedStockItem(null)
        } catch (error) {
            console.error('Error adding item to family:', error)
            alert(`Failed to add item: ${error.response?.data?.error || error.message}`)
        } finally {
            setAddingItem(false)
        }
    }

    const handleDeleteClick = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        try {
            await itemFamilyAPI.delete(id)
            navigate('/item-families')
        } catch (error) {
            console.error('Error deleting item family:', error)
            alert(`Failed to delete item family: ${error.response?.data?.message || error.message}`)
        } finally {
            setShowDeleteModal(false)
        }
    }

    if (loading || permissionsLoading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!hasPermission('stock.items.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to {mode === 'create' ? 'create' : 'update'} item families.
                </Alert>
            </Container>
        )
    }

    if (mode === 'update' && !itemFamily) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Item family not found</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/item-families')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    return (
        <Container className="py-4" fluid>
            <h1>{mode === 'create' ? 'Create Item Family' : 'Update Item Family'}</h1>
            <Form.Group className="mb-4" controlId="name">
                <Form.Label>Name*</Form.Label>
                <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Enter item family name"
                    autoComplete="off"
                    spellCheck="false"
                />
                {mode === 'create' && (
                    <Form.Text className="text-muted">
                        A descriptive name for this group of interchangeable items
                    </Form.Text>
                )}
            </Form.Group>

            {/* Member Stock Items section - only show in update mode */}
            {mode === 'update' && (
                <>
                    <h4>Member Stock Items</h4>

                    {/* Add Item Card */}
                    <div className="card mb-3">
                        <div className="card-header bg-body-secondary">
                            <h5 className="mb-0">Add Stock Item to Family</h5>
                        </div>
                        <div className="card-body">
                            <Row className="align-items-end">
                                <Col>
                                    <Form.Group controlId="stockItemSelect">
                                        <Form.Label className="fw-bold">Stock Item*</Form.Label>
                                        <AsyncSelect
                                            value={selectedStockItem}
                                            onChange={setSelectedStockItem}
                                            loadOptions={loadStockItemOptions}
                                            getOptionLabel={option => `${option.description}${option.gtin ? ` (${option.gtin})` : ''}`}
                                            getOptionValue={option => option._id}
                                            formatOptionLabel={(option, { inputValue, context }) => {
                                                if (context === 'value') {
                                                    return `${option.description}${option.gtin ? ` (${option.gtin})` : ''}`
                                                }
                                                return (
                                                    <span>
                                                        {highlightText(option.description, inputValue)}
                                                        {option.gtin ? <> ({highlightText(option.gtin, inputValue)})</> : ''}
                                                    </span>
                                                )
                                            }}
                                            noOptionsMessage={({ inputValue }) =>
                                                !inputValue || inputValue.length < 2
                                                    ? 'Type at least 2 characters to search...'
                                                    : 'No matching items found'
                                            }
                                            placeholder="Search by name or GTIN..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            isClearable={true}
                                            defaultOptions={false}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs="auto">
                                    <Button
                                        variant="success"
                                        onClick={handleAddItem}
                                        disabled={!selectedStockItem || addingItem}
                                    >
                                        {addingItem ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-plus me-2"></i>
                                                Add Item
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Member Items Table */}
                    {itemFamily.stock_items && itemFamily.stock_items.length > 0 ? (
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th style={{ width: '60px' }}>Image</th>
                                    <th>GTIN</th>
                                    <th>Name</th>
                                    <th>Manufacturer</th>
                                    <th>Categories</th>
                                    <th style={{ width: '100px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemFamily.stock_items.map((item) => (
                                    <tr key={item._id}>
                                        <td>
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.description || item.name}
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                />
                                            ) : null}
                                        </td>
                                        <td>{item.gtin ? <CopyableText value={item.gtin} toastLabel="GTIN" /> : ''}</td>
                                        <td>{item.description || item.name}</td>
                                        <td>{item.manufacturer_name || '-'}</td>
                                        <td>{item.categories_names?.join(', ') || '-'}</td>
                                        <td>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRemoveItem(item._id)}
                                            >
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <Alert variant="info">
                            No stock items assigned to this family yet. Use the form above to add items.
                        </Alert>
                    )}
                </>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mt-4">
                    {error}
                </Alert>
            )}

            <div className="mt-4">
                <Button
                    variant="success"
                    className="me-2"
                    onClick={submitHandler}
                >
                    {mode === 'create' ? 'Create' : 'Update'}
                </Button>
                {mode === 'update' && (
                    <Button
                        variant="danger"
                        className="me-2"
                        onClick={handleDeleteClick}
                    >
                        Delete
                    </Button>
                )}
                <Button
                    variant="outline-primary"
                    onClick={() => navigate('/item-families')}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the item family "${itemFamily?.name}"? Stock items in this family will be unassigned.`}
            />
        </Container>
    )
}

export default ItemFamilyUpdateScreen
